import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { createUser, updateUser } from '../../../../redux/users';
import * as Yup from 'yup';
import { Form, FormGroup, Button, Spinner } from 'reactstrap';
// import { updateUser } from '../../../../redux/users';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import VoicesGroup from './VoicesGroup';
import GroupForm from './GroupForm';
import axios from 'axios';

const validationSchema = Yup.object().shape({
  username: Yup.string().required('Username is required').nullable(true),
  email: Yup.string().email('Email is invalid').required('Email is required').nullable(true),
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters')
    .nullable(true),
  ph_number: Yup.string()
    .required('Phone number is required')
    .min(10, 'Phone number is invalid')
    .nullable(),
  original_number_of_chars: Yup.number()
    .required('Number of characters is required')
    .nullable(true),
  expiry_date: Yup.date()
    .required('Expiry date is required')
    .min(new Date(), 'Expiry date must be after today')
    .nullable(true),
  voices: Yup.array().required('Voices are required').nullable(true)
});

const UserForm = ({ user, userLoading }) => {
  const [voices2, setVoices2] = useState({});
  const [loading, setLoading] = useState(false);
  const getVoices = async () => {
    setLoading(true);
    const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/voice_names`);
    setVoices2(response.data);
    setLoading(false);
  };

  useEffect(() => {
    getVoices();
  }, []);

  const vendors = Object.keys(voices2);
  const dispatch = useDispatch();

  const handleUpdateUser = (data) => {
    dispatch(updateUser(data));
  };

  const handleCreateUser = (data) => {
    dispatch(createUser(data));
  };

  const navigate = useNavigate();

  const MySwal = withReactContent(Swal);

  const handleConfirmation = (handleUser, values, formType) => {
    MySwal.fire({
      title: 'Are you sure?',
      text: `${formType} user with email (${
        values.email ? values.email : userEmail
      }) and username (${values.username ? values.username : user.username})?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${formType} user!`
    })
      .then((result) => {
        if (result.isConfirmed) {
          handleUser(values);
          MySwal.fire(`${formType} user!`, `${formType} user successful`, 'success');
          navigate('/admin');
        } else {
          MySwal.fire('Cancelled', `${formType} user cancelled`, 'error');
        }
      })
      .then(() => {
        navigate('/admin');
      });
  };

  const [selectedVendor, setSelectedVendor] = useState(vendors[0]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [voices, setVoices] = useState([]);
  const [selectedVoices, setSelectedVoices] = useState([]);

  const voicesArray = vendors
    .map((vendor) => {
      return voices2[vendor].map((voice) => {
        return {
          ...voice,
          vendor,
          language: voice.language
        };
      });
    })
    .flat();
  const languages = [
    ...new Set(
      voicesArray.filter((voice) => voice.vendor === selectedVendor).map((voice) => voice.language)
    )
  ].map((language) => {
    return {
      language,
      voices: voicesArray.filter((voice) => voice.language === language)
    };
  });

  const filterVoices = (array, vendor, language) => {
    const filteredVoices = array.filter(
      (voice) => voice.vendor === vendor && voice.language === language
    );
    setVoices(filteredVoices);
    return filteredVoices;
  };

  useEffect(() => {
    filterVoices(voicesArray, selectedVendor, selectedLanguage);
  }, [selectedVendor, selectedLanguage]);

  const handleSelectVoice = (voice) => {
    const isSelected =
      voice.vendor === 'elevenlabs'
        ? selectedVoices
            .map((voice) => `${voice.name}-${voice.language}`)
            .includes(`${voice.name}-${voice.language}`)
        : selectedVoices.map((voice) => voice.name).includes(voice.name);
    if (isSelected) {
      setSelectedVoices(
        selectedVoices.filter((selectedVoice) => {
          if (voice.vendor === 'elevenlabs') {
            return (
              `${selectedVoice.name}-${selectedVoice.language}` !==
              `${voice.name}-${voice.language}`
            );
          } else {
            return selectedVoice.name !== voice.name;
          }
        })
      );
    } else {
      setSelectedVoices([...selectedVoices, voice]);
    }
  };

  const selectedVoicesMatch = (voice) => {
    if (voice.vendor === 'elevenlabs') {
      return selectedVoices
        .map((voice) => `${voice.name}-${voice.language}`)
        .includes(`${voice.name}-${voice.language}`);
    } else {
      return selectedVoices.map((voice) => voice.name).includes(voice.name);
    }
  };

  let initialFormValues = {};

  const userEmail = user?.email;

  if (user) {
    initialFormValues = {
      unique_value: {
        email: ''
      },
      username: '',
      email: '',
      password: '',
      ph_number: '',
      number_of_chars: '',
      original_number_of_chars: '',
      expiry_date: '',
      voices: []
    };
  } else {
    initialFormValues = {
      username: '',
      email: '',
      password: '',
      ph_number: '',
      original_number_of_chars: '',
      expiry_date: '',
      voices: []
    };
  }

  const clearAllVoices = () => {
    setSelectedVoices([]);
  };

  // if values are empty then set them null
  const handleNullValues = (values) => {
    if (values.expiry_date === '' || values.expiry_date === 'NaN/NaN/NaN') {
      values.expiry_date = null;
    }

    const nullValues = Object.keys(values).filter((key) => values[key] === '');
    nullValues.forEach((value) => (values[value] = null));
    return values;
  };

  const formatDate = (date) => {
    const newDate = new Date(date);
    const day = newDate.getDate();
    const month = newDate.getMonth() + 1;
    const year = newDate.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    if (user) {
      setSelectedVoices(user?.voices);
    }
  }, [user]);

  return (
    <>
      <Formik
        initialValues={initialFormValues}
        validationSchema={!user ? validationSchema : null}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          values.expiry_date = formatDate(values.expiry_date);
          if (user) {
            user && user.voices === selectedVoices
              ? (values.voices = selectedVoices)
              : (values.voices = selectedVoices);
            handleNullValues(values);
            handleConfirmation(handleUpdateUser, values, 'Update');
          } else {
            handleConfirmation(handleCreateUser, values, 'Create');
          }

          setSubmitting(false);
        }}>
        {(formik) => {
          const { values, errors, touched, isSubmitting, handleChange, handleSubmit } = formik;

          if (user) {
            values.unique_value = { email: userEmail };
          }
          return (
            <Form
              onSubmit={handleSubmit}
              className="mt-2 mb-2 p-2 d-flex flex-column  justify-content-center user-form">
              <FormGroup>
                <GroupForm
                  label="Username"
                  name="username"
                  type="text"
                  placeholder="Username"
                  value={values.username}
                  onChange={handleChange}
                  error={errors.username}
                  touched={touched.username}
                  user={user}
                  userLoading={userLoading}
                />
              </FormGroup>
              <FormGroup>
                <GroupForm
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="Email"
                  value={values.email}
                  onChange={handleChange}
                  error={errors.email}
                  touched={touched.email}
                  user={user}
                  userLoading={userLoading}
                />
              </FormGroup>
              <FormGroup>
                <GroupForm
                  label="Password"
                  name="password"
                  type="password"
                  placeholder="Password"
                  value={values.password}
                  onChange={handleChange}
                  error={errors.password}
                  touched={touched.password}
                  user={user}
                  userLoading={userLoading}
                />
              </FormGroup>
              <FormGroup>
                <GroupForm
                  label="Phone Number"
                  name="ph_number"
                  type="text"
                  placeholder="Phone Number"
                  value={values.ph_number}
                  onChange={handleChange}
                  error={errors.ph_number}
                  touched={touched.ph_number}
                  user={user}
                  userLoading={userLoading}
                />
              </FormGroup>
              <FormGroup>
                <GroupForm
                  label="original Number Of Characters"
                  name="original_number_of_chars"
                  type="number"
                  placeholder="Original Number Of Characters"
                  value={values.original_number_of_chars}
                  onChange={handleChange}
                  error={errors.original_number_of_chars}
                  touched={touched.original_number_of_chars}
                  user={user}
                  userLoading={userLoading}
                />
              </FormGroup>
              {user && (
                <FormGroup>
                  <GroupForm
                    label="Number Of Characters"
                    name="number_of_chars"
                    type="number"
                    placeholder="Number Of Characters"
                    value={values.number_of_chars}
                    onChange={handleChange}
                    error={errors.number_of_chars}
                    touched={touched.number_of_chars}
                    user={user}
                    userLoading={userLoading}
                  />
                </FormGroup>
              )}
              <FormGroup>
                <GroupForm
                  label="Expiry Date"
                  name="expiry_date"
                  type="date"
                  placeholder="Expiry Date"
                  value={values.expiry_date}
                  onChange={handleChange}
                  error={errors.expiry_date}
                  touched={touched.expiry_date}
                  user={user}
                  userLoading={userLoading}
                />
              </FormGroup>
              <VoicesGroup
                vendors={vendors}
                selectedVendor={selectedVendor}
                setSelectedVendor={setSelectedVendor}
                languages={languages}
                loading={loading}
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                voices={voices}
                selectedVoices={selectedVoices}
                setSelectedVoices={setSelectedVoices}
                selectedVoicesMatch={selectedVoicesMatch}
                handleSelectVoice={handleSelectVoice}
                clearAllVoices={clearAllVoices}
                formik={formik}
                values={values}
                errors={errors}
                touched={touched}
              />
              <FormGroup>
                <Button
                  color="primary"
                  type="submit"
                  disabled={isSubmitting}
                  className="submit-btn">
                  {isSubmitting && <Spinner size="sm" color="light" className="mr-2" />}
                  {user ? 'Update User' : 'Create User'}
                </Button>
              </FormGroup>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default UserForm;
